import { Link, useLocation, Outlet } from 'react-router-dom'
import {
  LayoutDashboard,
  Megaphone,
  Image,
  PenLine,
  Calendar,
  Lightbulb,
  History,
  BarChart3,
  Zap,
  Menu,
  X,
  Crown,
  ChevronRight,
} from 'lucide-react'
import { useState } from 'react'
import { useMarketingAccess } from '../../hooks/useMarketing'
import { MARKETING_NAV } from '../../lib/marketing/constants'
import { normalizePlanId } from '../../lib/plans'

const ICONS: Record<string, React.ReactNode> = {
  LayoutDashboard: <LayoutDashboard className="w-5 h-5" />,
  Megaphone: <Megaphone className="w-5 h-5" />,
  Image: <Image className="w-5 h-5" />,
  PenLine: <PenLine className="w-5 h-5" />,
  Calendar: <Calendar className="w-5 h-5" />,
  Lightbulb: <Lightbulb className="w-5 h-5" />,
  History: <History className="w-5 h-5" />,
  BarChart3: <BarChart3 className="w-5 h-5" />,
  Zap: <Zap className="w-5 h-5" />,
}

export function MarketingUpgrade() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-10 text-white shadow-xl">
        <Crown className="w-16 h-16 mx-auto mb-4 opacity-90" />
        <h1 className="text-3xl font-bold mb-3">Marketing Center</h1>
        <p className="text-indigo-100 mb-6">
          Pusat promosi digital untuk membuat, mengelola, dan membagikan materi promosi UMKM Anda.
          Fitur ini tersedia untuk paket Basic dan Premium.
        </p>
        <Link
          to="/subscription"
          className="inline-flex items-center gap-2 bg-white text-indigo-700 font-semibold px-6 py-3 rounded-xl hover:bg-indigo-50 transition-colors"
        >
          Upgrade Paket <ChevronRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  )
}

export function MarketingLayout() {
  const location = useLocation()
  const { data: access, isLoading } = useMarketingAccess()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const plan = normalizePlanId(access?.plan)
  const isPremium = plan === 'premium'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    )
  }

  if (!access?.hasAccess) {
    return <MarketingUpgrade />
  }

  const isActive = (path: string) =>
    path === '/marketing' ? location.pathname === '/marketing' : location.pathname.startsWith(path)

  const aiBoost = access.aiBoost
  const storage = access.storage

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex gap-6">
        <button
          type="button"
          className="lg:hidden fixed bottom-6 right-6 z-40 bg-indigo-600 text-white p-3 rounded-full shadow-lg"
          onClick={() => setSidebarOpen((v) => !v)}
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          } fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border border-gray-200 rounded-xl lg:rounded-xl shadow-lg lg:shadow-sm p-4 transition-transform lg:transition-none h-fit lg:sticky lg:top-20`}
        >
          <div className="mb-4 pb-4 border-b">
            <h2 className="font-bold text-gray-900">Marketing Center</h2>
            <p className="text-xs text-gray-500 mt-1">Pusat promosi digital</p>
          </div>
          <nav className="space-y-1">
            {MARKETING_NAV.map((item) => {
              const locked = 'premium' in item && item.premium && !isPremium
              return (
                <Link
                  key={item.to}
                  to={locked ? '/subscription' : item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.to)
                      ? 'bg-indigo-50 text-indigo-700'
                      : locked
                        ? 'text-gray-400 hover:bg-gray-50'
                        : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {ICONS[item.icon]}
                  <span className="flex-1">{item.label}</span>
                  {locked && <Crown className="w-4 h-4 text-amber-500" />}
                </Link>
              )
            })}
          </nav>

          {aiBoost && (
            <div className="mt-6 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold">
                <Zap className="w-4 h-4" /> AI Boost
              </div>
              <div className="mt-2 h-2 bg-amber-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all"
                  style={{ width: `${aiBoost.yearlyQuota ? Math.min(100, (aiBoost.yearlyUsed / aiBoost.yearlyQuota) * 100) : 0}%` }}
                />
              </div>
              <p className="text-xs text-amber-700 mt-1">
                {aiBoost.balance} kredit tersisa
              </p>
            </div>
          )}

          {storage && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs font-semibold text-gray-700">Penyimpanan</p>
              <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full"
                  style={{
                    width: storage.limit
                      ? `${Math.min(100, (storage.used / storage.limit) * 100)}%`
                      : `${Math.min(storage.used, 100)}%`,
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {storage.used} / {storage.limit ?? '∞'} materi
              </p>
            </div>
          )}
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/30 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
